/**
 * Feature flags for controlling experimental or conditional features
 */
import { isFlowTasteEnabled } from "@/lib/is-flow-taste-enabled";

export const featureFlags = {
  /**
   * Enable the **visual-only** Flow-taste surface (gallery card craft, shimmer
   * in-flight cards, graceful failed cards, prompt-shell scrim markers).
   *
   * Delegates to the single pure-build-constant reader
   * `isFlowTasteEnabled()` (resolved from `NEXT_PUBLIC_ENABLE_FLOW_TASTE`).
   * This is intentionally SEPARATE from the preview-model-picker gate so the
   * Flow-taste visuals can be promoted to production independently while every
   * FUNCTIONAL preview-only feature stays behind `isPreviewModelPickerEnabled()`.
   *
   * Cloudflare envs ship prod=`false` (until the Terry-gated flip) and
   * preview=`true`; the package.json deploy phases set the matching defaults.
   */
  enableFlowTaste: isFlowTasteEnabled(),

  /** Enable staging-only feature wiring */
  stagingFeatureGate:
    process.env.NEXT_PUBLIC_STAGE === "staging" || process.env.NEXT_PUBLIC_STAGE === "preview",

  /**
   * Enable browser-local edit dogfood on preview only
   * This exposes the WebGPU edit workspace to preview.aideco.us but not production
   */
  enableBrowserEditPreview:
    process.env.NEXT_PUBLIC_STAGE === "staging" || process.env.NEXT_PUBLIC_STAGE === "preview",

  // Main UI Components
  /**
   * Show the PromptBar component in the NewBoard interface
   * Set to false to hide the PromptBar while keeping it in the codebase
   */
  showPromptBar: true,

  /**
   * Show the mode indicator badge at the top of the board
   */
  showModeIndicator: false,

  /**
   * Show mode-specific controls (angle selector, style options, etc.)
   */
  showModeControls: false,

  /**
   * Show suggestion prompts when no images have been generated
   */
  showSuggestionPrompts: true,

  /**
   * Show keyboard shortcuts dialog
   */
  showKeyboardShortcuts: true,

  // PromptBar Features
  /**
   * Show image upload button in PromptBar toolbar
   */
  showImageUpload: true,

  /**
   * Show AI enhance button in PromptBar toolbar
   */
  showAiEnhance: true,

  /**
   * Show draft toggle in PromptBar (deprecated - quality selection handles this)
   */
  showDraftToggle: false,

  /**
   * Show clear button in PromptBar when images are generated
   */
  showClearButton: true,

  /**
   * Bypass credit checks in development (gives unlimited credits)
   * Only works when NODE_ENV is 'development'
   */
  devUnlimitedCredits: process.env.NODE_ENV === "development",

  // Billing & Monetization

  /**
   * Sync onboarding state with server persistence layer
   */
  enableOnboardingServerSync:
    process.env.NEXT_PUBLIC_STAGE === "staging" || process.env.NEXT_PUBLIC_STAGE === "preview",

  /**
   * Enable credits summary with low-balance signals
   */
  enableCreditsSummary:
    process.env.NEXT_PUBLIC_STAGE === "staging" || process.env.NEXT_PUBLIC_STAGE === "preview",

  /**
   * Enable Intercom widget boot and script injection.
   *
   * Build-time public flag resolved from `NEXT_PUBLIC_ENABLE_INTERCOM`. The
   * flag is intentionally opt-in: any value other than the literal string
   * `"true"` (including unset, empty, "false", or anything else) keeps Intercom
   * disabled. Preview and production Cloudflare envs ship with this value set
   * to `"false"` so the widget never boots, never injects its script, and
   * never touches `window.intercomSettings` / `window.Intercom`. Local
   * development can opt in by exporting `NEXT_PUBLIC_ENABLE_INTERCOM=true`
   * before building, which preserves the existing enabled-mode behavior for
   * regression testing.
   */
  enableIntercom: process.env.NEXT_PUBLIC_ENABLE_INTERCOM === "true",

  /**
   * Enable the paid monetization gate copy across pricing, marketing, and
   * onboarding surfaces.
   *
   * Build-time public flag resolved from `NEXT_PUBLIC_MONETIZATION_GATE`. It is
   * intentionally opt-in: any value other than the literal string `"true"`
   * (including unset, empty, or "false") keeps the gate OFF.
   *
   * - When **true**: pricing/marketing/onboarding show the paid-trial copy
   *   (3-day free trial, card required, subscription required, no free tier).
   *   This matches a future gate flip where the product requires payment.
   * - When **false** (default): the same surfaces show the free-to-start copy
   *   (the pre-monetization wording — "start free, no credit card required",
   *   a Free tier with daily free renders, etc.).
   *
   * Keep this aligned with the live server gate state so the copy never
   * contradicts what the product actually enforces.
   */
  enablePaidGate: process.env.NEXT_PUBLIC_MONETIZATION_GATE === "true",

  /**
   * Enable the Spatial Project feature surfaces (save-as-project from /try,
   * /project/[id] viewer, room detection/generation, share/export).
   *
   * Build-time public flag resolved from `NEXT_PUBLIC_ENABLE_SPATIAL_PROJECT`.
   * Opt-in: any value other than the literal string `"true"` keeps the
   * spatial project UI hidden. Preview ships enabled; production disabled
   * until validated and Terry-approved.
   */
  enableSpatialProject: process.env.NEXT_PUBLIC_ENABLE_SPATIAL_PROJECT === "true",
} as const;

/**
 * Type-safe feature flag accessor
 */
export function isFeatureEnabled(flag: keyof typeof featureFlags): boolean {
  return featureFlags[flag];
}
